import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectAllRoomAvailabilities } from '../../../core/store/reducers';
import { Room, RoomAvailability } from '../../../core/models/entities/room';
import { Instance } from 'flatpickr/dist/types/instance';
import {
  addDays,
  addMinutes,
  endOfMonth,
  format,
  isBefore,
  isEqual,
  isSameDay,
  isValid,
  isWithinInterval,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
  subMinutes,
} from 'date-fns';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  pairwise,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import { BaseOptions } from 'flatpickr/dist/types/options';
import { roomAvailabilitiesActions } from '../../../core/store/room-availabilities';
import { Actions, ofType } from '@ngrx/effects';
import { Booking, RateType } from '../../../core/models/entities/booking';
import { DateInputComponent } from '../../../shared/components/date-input/date-input.component';
import { BookingType } from '../../../core/enums/booking-type';
import { Availability } from '../../../core/models/entities/availability';

enum ReservationType {
  SingleDay = 'single_day',
  MultipleDays = 'multiple_days',
  Hourly = 'hourly',
}

@Component({
  selector: 'app-private-booking-date-subform',
  template: `
    <!-- Type de réservation : select compact -->
    <div class="mb-4 flex items-baseline gap-4">
      <label for="reservation_type" class="w-32 shrink-0 text-sm font-medium"
        >{{ 'bookings.date-subform.type-label' | transloco }}</label
      >

      <app-select-input
        [control]="form.controls.reservation_type"
        [options]="reservationTypeOptions"
        name="reservation_type"
        size="small"
        class="grow"
        [marginBottom]="false"
      ></app-select-input>
    </div>

    <!-- Ligne Début : Date (+ Heure si hourly) -->
    <div class="mb-4 flex items-baseline gap-4">
      <label for="dates" class="w-32 shrink-0 text-sm font-medium">{{ 'bookings.date-subform.start-label' | transloco }}</label>
      <div class="flex flex-1 items-center gap-2">
        <div class="flex-1" [class.cursor-not-allowed]="disabled()">
          <app-date-input
            name="dates"
            [control]="form.controls.dates"
            [flatpickrOptions]="flatpickrOptions()"
            [disabled]="disabled() || loadingAvailabilities()"
            [loading]="loadingAvailabilities()"
            [placeholder]="
              loadingAvailabilities()
                ? ('bookings.date-subform.loading-availabilities' | transloco)
                : disabled()
                  ? ('bookings.date-subform.select-room-placeholder' | transloco)
                  : ('bookings.date-subform.date-placeholder' | transloco)
            "
            [marginBottom]="false"
            [marginTop]="false"
            size="small"
          ></app-date-input>
        </div>
        @if (form.value.reservation_type === ReservationType.Hourly) {
          <app-select-input
            [control]="form.controls.start_time"
            name="start_time"
            [placeholder]="'bookings.date-subform.time-placeholder' | transloco"
            size="small"
            [inline]="true"
            [options]="startTimeOptions()"
            [marginBottom]="false"
            [hidePleaseSelect]="true"
            [loading]="loadingTimeOptions() || loadingAvailabilities()"
            [disabled]="loadingTimeOptions() || loadingAvailabilities() || disabled() || !form.value.dates?.[0]"
            selectClass="w-28"
          ></app-select-input>
        }
      </div>
    </div>

    <!-- Ligne Fin : Heure (si hourly) -->
    @if (form.value.reservation_type === ReservationType.Hourly) {
      <div class="mb-4 flex items-baseline gap-4">
        <label for="end_time" class="w-32 shrink-0 text-sm font-medium"
          >{{ 'bookings.date-subform.end-label' | transloco }}</label
        >
        <div class="flex flex-1 items-center gap-2">
          <div class="flex-1">
            <app-date-input
              [control]="endDateDisplay"
              [disabled]="true"
              [marginBottom]="false"
              [marginTop]="false"
              size="small"
            ></app-date-input>
          </div>
          <app-select-input
            [control]="form.controls.end_time"
            name="end_time"
            [placeholder]="'bookings.date-subform.time-placeholder' | transloco"
            size="small"
            [inline]="true"
            [options]="endTimeOptions()"
            [marginBottom]="false"
            [hidePleaseSelect]="true"
            [loading]="loadingTimeOptions() || loadingAvailabilities()"
            [disabled]="loadingTimeOptions() || loadingAvailabilities() || disabled() || !form.value.dates?.[0]"
            selectClass="w-28"
          ></app-select-input>
        </div>
      </div>
    }

  `,
  standalone: false,
})
export class PrivateBookingDateSubformComponent implements OnInit {
  protected readonly ReservationType = ReservationType;

  private dateInput = viewChild(DateInputComponent);
  private actions$ = inject(Actions);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private translocoService = inject(TranslocoService);

  room = input<Room>();
  booking = input<Booking>();
  disabled = input<boolean>(false);
  initialStartDate = input<Date>();
  initialEndDate = input<Date>();

  form = new FormGroup({
    reservation_type: new FormControl<ReservationType>(
      ReservationType.Hourly,
      [Validators.required],
    ),
    dates: new FormControl<Date[]>([], [Validators.required]),
    start_time: new FormControl<string>({ value: null, disabled: true }, [
      Validators.required,
    ]),
    end_time: new FormControl<string>({ value: null, disabled: true }, [
      Validators.required,
    ]),
  });

  endDateDisplay = new FormControl<Date[]>([]);

  reservationTypeOptions: { value: ReservationType; label: string }[] = [];

  flatpickrOptions = signal<Partial<BaseOptions>>({
    inline: false,
    mode: 'single',
    onYearChange: (_: any, __: any, self: Instance) => {
      this.findAvailabilitiesForMonth(self.currentYear, self.currentMonth);
    },
    onMonthChange: (_: any, __: any, self: Instance) => {
      this.findAvailabilitiesForMonth(self.currentYear, self.currentMonth);
    },
  });

  loadingAvailabilities = signal<boolean>(false);
  hasDate = toSignal(
    this.form.controls.dates.valueChanges.pipe(
      startWith(this.form.controls.dates.value),
      map((dates) => !!dates?.length),
    ),
    { initialValue: false },
  );

  loadingTimeOptions = computed(() =>
    this.loadingAvailabilities() ||
    (!!this.room() && !!this.form.value.dates?.[0] && this.startTimeOptions().length === 0),
  );

  timeslots = signal<any[]>([]);
  private startTimeSignal = signal<string | null>(null);
  private startDateSignal = signal<Date | null>(null);

  startTimeOptions = computed(() => {
    const options: { label: string; value: string }[] = [];
    for (const timeslot of this.timeslots()) {
      let currentTime = this.roundToQuarterHour(new Date(timeslot.start));
      const roundedEnd = this.roundToQuarterHour(new Date(timeslot.end));
      const lastPossibleTime = subMinutes(roundedEnd, 15);
      do {
        options.push({
          label: format(currentTime, 'HH:mm'),
          value: format(currentTime, 'HH:mm'),
        });
        currentTime = addMinutes(currentTime, 15);
      } while (!isBefore(lastPossibleTime, currentTime));
    }
    return options;
  });

  endTimeOptions = computed(() => {
    const startTime = this.startTimeSignal();
    const timeslots = this.timeslots();
    const startDate = this.startDateSignal();

    if (!startTime || !timeslots?.length) return [];

    const start = parse(startTime, 'HH:mm', startDate ?? new Date());
    const timeslot = timeslots.find((tm: any) =>
      isWithinInterval(start, {
        start: new Date(tm.start),
        end: new Date(tm.end),
      }),
    );
    if (!timeslot) return [];

    const options: { label: string; value: string }[] = [];
    let currentTime = addMinutes(start, 15);
    const lastPossibleTime = this.roundToQuarterHour(new Date(timeslot.end));
    do {
      options.push({
        label: format(currentTime, 'HH:mm'),
        value: format(currentTime, 'HH:mm'),
      });
      currentTime = addMinutes(currentTime, 15);
    } while (!isBefore(lastPossibleTime, currentTime));

    return options;
  });

  private lastAvailabilityFetch$ = new BehaviorSubject<{
    roomId: number;
    from: string;
    to: string;
  } | null>(null);

  private roomAvailabilities$ = this.store.select(selectAllRoomAvailabilities);

  private reservationType$ =
    this.form.controls.reservation_type.valueChanges.pipe(
      startWith(this.form.controls.reservation_type.value),
    );
  private bookingType$ = this.reservationType$.pipe(
    map((type) =>
      type === ReservationType.MultipleDays
        ? BookingType.Consecutive
        : BookingType.Single,
    ),
    distinctUntilChanged(),
  );
  private availabilitiesFindAllSuccess$ = this.actions$.pipe(
    ofType(roomAvailabilitiesActions.findAllSuccess),
  );

  constructor() {
    this.setupSubscriptions();
    this.setupEffects();
    this.setupInitializeFromCalendarClick();
  }

  private setupSubscriptions() {
    this.setupTimeslotsFromDateAndAvailabilities();
    this.setupSyncSignalsFromFormControls();
    this.setupReservationTypeChange();
    this.setupFlatpickrAvailabilities();
    this.setupResetInvalidTimes();
    this.setupSyncEndDateDisplay();
    this.setupAutoSelectStartTime();
    this.setupAutoSelectEndTime();
    this.setupTimeControlsEnable();
  }

  private setupTimeslotsFromDateAndAvailabilities() {
    combineLatest([
      this.form.controls.dates.valueChanges.pipe(
        startWith(this.form.controls.dates.value),
      ),
      this.roomAvailabilities$,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([dates, availabilities]) => {
        const startDate = dates?.[0] ?? null;
        if (!startDate) {
          this.timeslots.set([]);
          return;
        }
        const timeslots = availabilities.find((av: Availability) =>
          isSameDay(new Date(av.date), startDate),
        )?.timeslots ?? [];
        this.timeslots.set(timeslots);
      });
  }

  private setupSyncSignalsFromFormControls() {
    this.form.controls.start_time.valueChanges
      .pipe(startWith(this.form.controls.start_time.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.startTimeSignal.set(value));

    this.form.controls.dates.valueChanges
      .pipe(startWith(this.form.controls.dates.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(dates => this.startDateSignal.set(dates?.[0] ?? null));
  }

  private setupEffects() {
    this.setupLoadAvailabilitiesOnRoomChange();
    this.setupInitializeFormFromBooking();
  }

  private setupLoadAvailabilitiesOnRoomChange() {
    effect(() => {
      const room = this.room();
      if (!room) return;

      untracked(() => {
        this.loadingAvailabilities.set(true);

        let targetDate: Date;
        if (this.dateInput()?.flatpickrInstance) {
          targetDate = new Date(
            this.dateInput().flatpickrInstance.currentYear,
            this.dateInput().flatpickrInstance.currentMonth,
          );
        } else if (isValid(this.form.value.dates?.[0])) {
          targetDate = this.form.value.dates[0];
        } else if (typeof this.booking()?.start === 'string') {
          targetDate = parseISO(this.booking().start);
        } else {
          targetDate = new Date();
        }

        const from = DatetimeHelper.dateToApiFormat(startOfMonth(targetDate));
        const to = DatetimeHelper.dateToApiFormat(endOfMonth(targetDate));

        const lastFetch = this.lastAvailabilityFetch$.value;
        const paramsChanged =
          !lastFetch ||
          lastFetch.roomId !== room.id ||
          lastFetch.from !== from ||
          lastFetch.to !== to;

        if (paramsChanged) {
          this.lastAvailabilityFetch$.next({ roomId: room.id, from, to });
          this.store.dispatch(
            roomAvailabilitiesActions.findAll({
              rooms_ids: [room.id],
              from,
              to,
              ignore_booking_id: this.booking()?.id,
              private_booking: true,
              ignore_session_id: this.booking()?.session_id,
            }),
          );
        } else {
          this.loadingAvailabilities.set(false);
        }
      });
    });
  }

  private setupInitializeFormFromBooking() {
    effect(() => {
      const booking = this.booking();
      if (!booking?.start) return;

      const reservationType = this.getReservationTypeFromBooking(booking);

      if (reservationType === ReservationType.MultipleDays && !this.room()) {
        return;
      }

      const start = this.parseBookingDate(booking.start);
      const end =
        reservationType === ReservationType.MultipleDays
          ? this.toLastBookedDay(this.parseBookingDate(booking.end))
          : this.parseBookingDate(booking.end);
      const bookingType =
        reservationType === ReservationType.MultipleDays
          ? BookingType.Consecutive
          : BookingType.Single;

      if (this.isBookingAlreadyLoaded(reservationType, start, end)) return;

      this.form.controls.reservation_type.patchValue(reservationType, {
        emitEvent: false,
      });
      untracked(() => this.updateCalendarMode(bookingType));

      if (reservationType === ReservationType.MultipleDays) {
        this.form.patchValue({ dates: [start, end] });
      } else {
        this.form.patchValue({ dates: [start] });
        if (reservationType === ReservationType.Hourly) {
          this.prefillTimesAfterOptionsLoaded(start, end);
        }
      }
    });
  }

  private setupTimeControlsEnable() {
    combineLatest([
      this.reservationType$,
      this.form.controls.dates.valueChanges.pipe(
        startWith(this.form.controls.dates.value),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([reservationType, dates]) => {
        const isHourly = reservationType === ReservationType.Hourly;
        const hasDate = !!dates?.[0];
        if (isHourly && hasDate) {
          this.form.controls.start_time.enable({ emitEvent: false });
          this.form.controls.end_time.enable({ emitEvent: false });
        } else {
          this.form.controls.start_time.disable({ emitEvent: false });
          this.form.controls.end_time.disable({ emitEvent: false });
        }
      });
  }

  private setupSyncEndDateDisplay() {
    this.form.controls.dates.valueChanges
      .pipe(
        startWith(this.form.controls.dates.value),
        map((dates) => dates?.[0] ?? null),
        filter((start) => !!start),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((start) => {
        this.endDateDisplay.setValue([start]);
      });
  }

  private setupAutoSelectStartTime() {
    combineLatest([
      this.form.controls.dates.valueChanges.pipe(
        startWith(this.form.controls.dates.value),
      ),
      this.reservationType$,
      this.form.controls.start_time.valueChanges.pipe(
        startWith(this.form.controls.start_time.value),
      ),
      this.roomAvailabilities$,
    ])
      .pipe(
        map(([_, type, currentValue]) => ({
          options: this.startTimeOptions(),
          type,
          currentValue,
        })),
        filter(
          ({ options, type, currentValue }) =>
            type === ReservationType.Hourly &&
            options.length > 0 &&
            !currentValue,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ options }) => {
        const now = new Date();
        now.setMinutes(0, 0, 0);
        const nextHour = format(addMinutes(now, 60), 'HH:mm');
        const preferredStart =
          options.find((o: any) => o.value >= nextHour) ?? options[0];
        this.form.controls.start_time.patchValue(preferredStart.value);
      });
  }

  private setupAutoSelectEndTime() {
    combineLatest([
      this.form.controls.start_time.valueChanges.pipe(
        startWith(this.form.controls.start_time.value),
      ),
      this.reservationType$,
      this.form.controls.end_time.valueChanges.pipe(
        startWith(this.form.controls.end_time.value),
      ),
      this.roomAvailabilities$,
    ])
      .pipe(
        map(([_, type, currentValue]) => ({
          options: this.endTimeOptions(),
          type,
          currentValue,
        })),
        filter(
          ({ options, type, currentValue }) =>
            type === ReservationType.Hourly &&
            options.length > 0 &&
            !currentValue,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ options }) => {
        const defaultIndex = Math.min(3, options.length - 1);
        this.form.controls.end_time.patchValue(options[defaultIndex].value);
      });
  }


  private setupReservationTypeChange() {
    this.bookingType$
      .pipe(
        startWith(BookingType.Single),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([previousType, currentType]) => {
        const isInitializingFromBooking =
          this.booking()?.start && this.form.value.dates?.length >= 2;

        if (
          this.shouldResetDatesForConsecutive(
            previousType,
            currentType,
            isInitializingFromBooking,
          )
        ) {
          this.resetDatesAndKeepCurrentMonth();
        }

        if (this.shouldKeepOnlyFirstDate(previousType, currentType)) {
          this.form.controls.dates.patchValue([this.form.value.dates[0]]);
        }

        this.updateCalendarMode(currentType);
      });

    this.setupAutoSelectTodayOnHourly();
  }

  private setupAutoSelectTodayOnHourly() {
    this.reservationType$
      .pipe(
        filter(
          (type) =>
            type === ReservationType.Hourly &&
            !this.form.value.dates?.length &&
            !!this.room(),
        ),
        switchMap(() =>
          this.roomAvailabilities$.pipe(
            filter((avs) => avs.length > 0),
            take(1),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((availabilities) => {
        if (this.form.value.dates?.length) return;

        const today = startOfDay(new Date());
        const todayAvailable = availabilities.find(
          (av: Availability) =>
            isSameDay(new Date(av.date), today) &&
            av.status !== RoomAvailability.Occupied,
        );
        if (todayAvailable) {
          this.form.controls.dates.patchValue([today]);
        }
      });
  }

  private setupFlatpickrAvailabilities() {
    this.availabilitiesFindAllSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingAvailabilities.set(false);
        this.deselectUnavailableDates();
        this.updateFlatpickrAvailabilities();
      });

    this.reservationType$
      .pipe(
        map((type) => type !== ReservationType.Hourly),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.deselectUnavailableDates();
        this.updateFlatpickrAvailabilities();
      });
  }

  private setupResetInvalidTimes() {
    this.setupResetInvalidEndTime();
    this.setupResetInvalidStartTime();
  }

  private setupResetInvalidEndTime() {
    combineLatest([
      this.form.controls.end_time.valueChanges.pipe(
        startWith(this.form.controls.end_time.value),
      ),
      this.form.controls.start_time.valueChanges.pipe(
        startWith(this.form.controls.start_time.value),
      ),
    ])
      .pipe(
        map(([endTime, _]) => ({ endTime, options: this.endTimeOptions() })),
        filter(
          ({ endTime, options }) =>
            !!endTime && !options.some((o: any) => o.value === endTime),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.form.controls.end_time.patchValue(null);
      });
  }

  private setupResetInvalidStartTime() {
    combineLatest([
      this.form.controls.start_time.valueChanges.pipe(
        startWith(this.form.controls.start_time.value),
      ),
      this.form.controls.dates.valueChanges.pipe(
        startWith(this.form.controls.dates.value),
      ),
    ])
      .pipe(
        map(([startTime, _]) => ({
          startTime,
          options: this.startTimeOptions(),
        })),
        filter(
          ({ startTime, options }) =>
            !!startTime && !options.some((o: any) => o.value === startTime),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.form.controls.start_time.patchValue(null);
        this.form.controls.end_time.patchValue(null);
      });
  }


  private shouldResetDatesForConsecutive(
    previousType: BookingType,
    currentType: BookingType,
    isInitializingFromBooking: boolean,
  ): boolean {
    return (
      previousType === BookingType.Single &&
      currentType === BookingType.Consecutive &&
      this.form.value.dates?.length > 0 &&
      !isInitializingFromBooking
    );
  }

  private shouldKeepOnlyFirstDate(
    previousType: BookingType,
    currentType: BookingType,
  ): boolean {
    return (
      previousType === BookingType.Consecutive &&
      currentType === BookingType.Single &&
      this.form.value.dates?.length > 1
    );
  }

  private resetDatesAndKeepCurrentMonth() {
    const currentMonth = this.dateInput()?.flatpickrInstance
      ? new Date(
          this.dateInput().flatpickrInstance.currentYear,
          this.dateInput().flatpickrInstance.currentMonth,
        )
      : this.form.value.dates[0];

    this.form.controls.dates.patchValue([]);

    if (currentMonth && this.dateInput()?.flatpickrInstance) {
      this.dateInput().flatpickrInstance.jumpToDate(currentMonth, false);
    }
  }

  private updateCalendarMode(bookingType: BookingType) {
    const calendarMode =
      bookingType === BookingType.Consecutive ? 'range' : 'single';
    this.flatpickrOptions.set({ ...this.flatpickrOptions(), mode: calendarMode });
  }

  private getReservationTypeFromBooking(booking: Booking): ReservationType {
    if (booking?.duration) {
      if (booking.duration.rate === RateType.Hourly)
        return ReservationType.Hourly;
      if (booking.duration.quantity === 1) return ReservationType.SingleDay;
      return ReservationType.MultipleDays;
    }
    return ReservationType.SingleDay;
  }

  private parseBookingDate(date: string | Date): Date {
    return typeof date === 'string' ? parseISO(date) : date;
  }

  private toLastBookedDay(end: Date): Date {
    return this.room()?.max_end_next_day ? subDays(end, 1) : end;
  }

  private isBookingAlreadyLoaded(
    reservationType: ReservationType,
    start: Date,
    end: Date,
  ): boolean {
    return (
      reservationType === this.form.value.reservation_type &&
      isEqual(start, this.form.value.dates?.[0] ?? null) &&
      isEqual(
        end,
        this.form.value.dates?.[1] ?? this.form.value.dates?.[0] ?? null,
      )
    );
  }

  ngOnInit() {
    this.translocoService.selectTranslation().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reservationTypeOptions = [
        { value: ReservationType.SingleDay, label: this.translocoService.translate('bookings.date-subform.type-single-day') },
        { value: ReservationType.MultipleDays, label: this.translocoService.translate('bookings.date-subform.type-multiple-days') },
        { value: ReservationType.Hourly, label: this.translocoService.translate('bookings.date-subform.type-hourly') },
      ];
    });
  }

  private setupInitializeFromCalendarClick() {
    let initialized = false;

    effect(() => {
      const initialStart = this.initialStartDate();
      const initialEnd = this.initialEndDate();

      if (initialized || !initialStart || !initialEnd || this.booking()?.start) return;
      initialized = true;

      this.form.controls.reservation_type.patchValue(ReservationType.Hourly);
      this.form.controls.dates.patchValue([initialStart]);

      // Charger les disponibilités du mois de la date cliquée si elles ne sont pas déjà disponibles
      untracked(() => {
        this.loadingAvailabilities.set(true);
        this.findAvailabilitiesForMonth(initialStart.getFullYear(), initialStart.getMonth());
      });

      this.prefillTimesAfterOptionsLoaded(initialStart, initialEnd);
    });
  }

  private prefillTimesAfterOptionsLoaded(start: Date, end: Date) {
    this.roomAvailabilities$
      .pipe(
        map(() => this.startTimeOptions()),
        filter((options) => options.length > 0),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.form.controls.start_time.patchValue(format(start, 'HH:mm'));
        setTimeout(() => {
          this.form.controls.end_time.patchValue(format(end, 'HH:mm'));
        });
      });
  }

  private roundToQuarterHour(date: Date): Date {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;

    if (roundedMinutes === 60) {
      rounded.setHours(rounded.getHours() + 1);
      rounded.setMinutes(0);
    } else {
      rounded.setMinutes(roundedMinutes);
    }
    rounded.setSeconds(0);
    rounded.setMilliseconds(0);

    return rounded;
  }

  private findAvailabilitiesForMonth(year: number, month: number) {
    const room = this.room();
    if (!room) return;

    this.store.dispatch(
      roomAvailabilitiesActions.findAll({
        rooms_ids: [room.id],
        from: DatetimeHelper.dateToApiFormat(
          startOfMonth(new Date(year, month)),
        ),
        to: DatetimeHelper.dateToApiFormat(endOfMonth(new Date(year, month))),
        ignore_booking_id: this.booking()?.id,
        private_booking: true,
        ignore_session_id: this.booking()?.session_id,
      }),
    );
  }

  private deselectUnavailableDates() {
    const dates = this.form.controls.dates.value;
    if (!dates?.length) return;

    firstValueFrom(this.roomAvailabilities$).then((roomAvailabilities) => {
      const allDay =
        this.form.value.reservation_type !== ReservationType.Hourly;

      const hasUnavailableDate = dates.some((date) => {
        const availability = roomAvailabilities.find((av: any) =>
          isSameDay(new Date(av.date), date),
        );
        if (!availability) {
          // Si on n'a pas de données pour ce mois, on ne peut pas conclure que la date est indisponible
          const hasDataForMonth = roomAvailabilities.some((av: any) => {
            const avDate = new Date(av.date);
            return avDate.getFullYear() === date.getFullYear() && avDate.getMonth() === date.getMonth();
          });
          return hasDataForMonth;
        }
        return allDay
          ? availability.status === RoomAvailability.Occupied ||
              availability.status === RoomAvailability.Partial
          : availability.status === RoomAvailability.Occupied;
      });

      if (hasUnavailableDate) {
        this.form.controls.dates.patchValue([]);
        this.form.controls.start_time.patchValue(null);
        this.form.controls.end_time.patchValue(null);
      }
    });
  }

  private updateFlatpickrAvailabilities() {
    firstValueFrom(this.roomAvailabilities$).then((roomAvailabilities) => {
      const allDay = this.form.value.reservation_type !== ReservationType.Hourly;

      this.flatpickrOptions.set({
        ...this.flatpickrOptions(),
        onDayCreate: (_: any, __: any, ___: any, dateElem?: any) => {
          const date = new Date(dateElem.dateObj);
          const availability = roomAvailabilities.find((av: any) =>
            isSameDay(new Date(av.date), date),
          );

          if (availability) {
            dateElem.className +=
              availability.status === RoomAvailability.Free
                ? ' free'
                : availability.status === RoomAvailability.Partial && !allDay
                  ? ' partial'
                  : ' occupied';
          }
        },
        disable: [
          (date: Date) => {
            return !!roomAvailabilities
              .filter((av: any) =>
                allDay
                  ? av.status === RoomAvailability.Occupied ||
                    av.status === RoomAvailability.Partial
                  : av.status === RoomAvailability.Occupied,
              )
              .find((av: any) => isSameDay(date, parseISO(av.date)));
          },
        ],
      });
    });
  }

  validate(): boolean {
    this.form.markAllAsTouched();

    if (!this.form.controls.dates.value?.length) {
      return false;
    }

    const bookingType =
      this.form.value.reservation_type === ReservationType.MultipleDays
        ? BookingType.Consecutive
        : BookingType.Single;

    if (
      bookingType === BookingType.Consecutive &&
      this.form.controls.dates.value?.length < 2
    ) {
      return false;
    }

    return this.form.valid;
  }

  getFormData(): {
    start: Date;
    end: Date;
    all_day: boolean;
  } {
    const dates = this.form.controls.dates.value;
    const reservationType = this.form.value.reservation_type;
    let start: Date;
    let end: Date;
    let allDay = true;

    if (reservationType === ReservationType.MultipleDays) {
      start = startOfDay(dates[0]);
      end = startOfDay(dates[1]);
    } else if (reservationType === ReservationType.Hourly) {
      allDay = false;
      const startTime = this.form.controls.start_time.value;
      const endTime = this.form.controls.end_time.value;
      start = parse(startTime, 'HH:mm', dates[0]);
      end = parse(endTime, 'HH:mm', dates[0]);
      if (isBefore(end, start)) {
        end = addDays(end, 1);
      }
    } else {
      start = startOfDay(dates[0]);
      end = startOfDay(dates[0]);
    }

    return { start, end, all_day: allDay };
  }

  allDay(): boolean {
    return this.form.value.reservation_type !== ReservationType.Hourly;
  }
}
